"use client";

import type { Media } from "@mw/shared/payload-types";
import type { ILink } from "@ui/components/core/Link";

import NextImage from "next/image";
import { EventName } from "@mw/shared/analyticsEventNames";
import { BlockBreadcrumbsV2 } from "@/app/_blocks/BlockBreadcrumbsv2";
import { LinkTracked } from "@ui/components/core/LinkTracked";
import { Noise } from "@ui/components/core/Noise/Noise";
import { SeamlessTexture } from "@ui/components/core/SeamlessTexture/SeamlessTexture";
import { ChladniHoverContainer } from "@ui/components/core/Chladni/ChladniHoverContainer";
import { ChladniHover } from "@ui/components/core/Chladni/ChladniHover";
import { getImageProps } from "@utilities/payload";
import { sizes } from "@ui/lib/sizes";

const CHLADNI_PATTERN = {
  mode: "default" as const,
  a: 1,
  b: 1,
  m: 2.6,
  n: 3,
  quality: 121,
  origin: [-2.5, 2.5] as [number, number],
  length: 1.5,
  anchor: "center" as const,
  symmetry: "quadrant" as const,
  hover: { a: -0.7 },
};

export interface HeroTextureProps {
  title?: string | null;
  description?: string | null;
  breadcrumbs?: { title: string; href?: string }[];
  primaryLink?: ILink | null;
  secondaryLink?: ILink | null;
  backgroundImage?: Media;
  noiseEnabled?: boolean;
}

export function HeroTextureClient({
  title,
  description,
  breadcrumbs,
  primaryLink,
  secondaryLink,
  backgroundImage,
  noiseEnabled = true,
}: HeroTextureProps) {
  const imageProps = getImageProps(backgroundImage, { placeholder: true, fill: true });

  return (
    <div className='tw-space-y-6 lg:tw-grid tw-grid-cols-2 lg:tw-space-y-0'>
      {/* Left side — content */}
      <div className='tw-flex tw-flex-1 tw-flex-col tw-gap-5 tw-py-8 md:tw-py-12 tw-px-8 md:tw-px-12'>
        <div className='tw-flex tw-flex-col tw-gap-5 tw-mt-auto'>
          {breadcrumbs && <BlockBreadcrumbsV2 links={breadcrumbs} size='small' hidenOnMobileAfter={0} />}
          <h1 className='tw-max-w-3xl tw-type-5xl'>{title}</h1>
          <div className='tw-max-w-lg'>
            <p className='tw-text-light tw-type-base tw-text-gray-500'>{description}</p>
            {(primaryLink || secondaryLink) && (
              <div className='tw-flex tw-flex-wrap tw-gap-3 tw-pt-10'>
                {primaryLink && (
                  <LinkTracked
                    {...primaryLink}
                    asButton
                    buttonVariants={{ variant: "primary", size: "lg" }}
                    eventName={EventName.mw_hero_primary_link}
                  />
                )}
                {secondaryLink && (
                  <LinkTracked
                    {...secondaryLink}
                    asButton
                    buttonVariants={{ variant: "elevated", size: "lg" }}
                    eventName={EventName.mw_hero_secondary_link}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right side — texture rectangle */}
      <ChladniHoverContainer
        chladni={CHLADNI_PATTERN}
        aria-hidden='true'
        className='tw-relative tw-overflow-hidden tw-min-h-[400px] md:tw-min-h-[600px]'
      >
        {imageProps && (
          <NextImage
            {...imageProps}
            alt=''
            className='tw-object-cover'
            priority
            sizes={sizes("100vw", { lg: "50vw" })}
          />
        )}
        {noiseEnabled && (
          <>
            <Noise />
            <SeamlessTexture />
          </>
        )}
        <div className='tw-absolute tw-inset-0'>
          <ChladniHover width={600} height={600} {...CHLADNI_PATTERN} />
        </div>
      </ChladniHoverContainer>
    </div>
  );
}
